import { connectedApiService as api } from '../../index'
import { canonicalFlightKey } from './flightNervePure'

// ── Why the last lookup came back empty ─────────────────────────────────────
// Every function here returns null on failure, so the resolver — and therefore
// #flight-tracking-errors — cannot tell "FlightNerve has no such flight" from
// "FlightNerve 500'd / is over quota / timed out". Those are opposite problems: the first
// is a fact about the flight, the second is our supplier being down while the flight is
// perfectly real. Reporting both as "Flight not found" is what made the channel
// unreadable.
//
// So each failure is recorded here, under the `flightNerve` key the API now returns
// (see flightNerve.js). The reporter drains this when it posts, and only within a short
// window — a failure from a lookup ten minutes ago says nothing about this one.
const MAX_TRACKED_FAILURES = 10
const FAILURE_RELEVANCE_MS = 30000

let recentFailures = []

// ── Whose failure is it? ────────────────────────────────────────────────────
// This buffer is module-level and DRAINED by whoever reports first, which is fine while
// one lookup runs at a time and wrong the moment two do. Two is not an edge case: a VIP
// TRANSIT booking renders an arrival card and a departure card together, and an add-on
// booking has an arrival and a departure flight field on the same form — two lookups,
// two independent reporters (one per leg, by design), one shared buffer. Whichever leg
// reported first drained BOTH legs' entries, so it could carry the other flight's 500s
// while the second leg posted with no explanation at all.
//
// So entries carry the flight they belong to and are only drained by that flight.
// canonicalFlightKey is the same normaliser the reporter dedupes on, so "AA 341",
// "aa341" and "AA0341" are one flight on both sides.
const flightTagOf = value => {
  const text = String(value == null ? '' : value).trim()
  return text ? canonicalFlightKey(text) : null
}

// An abort is our own doing, not an upstream problem. Recording it would put a phantom
// failure in the Slack suffix for a lookup the user themselves superseded.
export const isAbortError = error =>
  Boolean(error) && (error.name === 'AbortError' || error.code === 20)

const recordFailure = (endpoint, error, flight) => {
  if (isAbortError(error)) return
  const detail = (error && error.flightNerve) || {}
  if (detail.code === 'airport_mismatch') rememberAirportMismatch(flight)
  recentFailures.push({
    endpoint,
    code: detail.code || 'unknown',
    status: detail.status || null,
    at: Date.now(),
    flight: flightTagOf(flight),
  })
  if (recentFailures.length > MAX_TRACKED_FAILURES) recentFailures.shift()
}

// Not every empty result is an HTTP failure. FlightNerve can answer 200 with a flight it
// knows the route for but has no times for at all, which our mapper correctly reduces to
// noData — nothing to render — but which is NOT the same as "no such flight". Without
// this the two are indistinguishable in the channel: both arrive with no suffix, and the
// agent goes hunting for a typo that isn't there.
//
// Same buffer as the failures, so one report carries one combined explanation.
export const recordFlightNerveNote = (endpoint, code, status = 200, flight) => {
  recentFailures.push({
    endpoint,
    code: code || 'unknown',
    status,
    at: Date.now(),
    flight: flightTagOf(flight),
  })
  if (recentFailures.length > MAX_TRACKED_FAILURES) recentFailures.shift()
}

// Drains: a failure is attributed to ONE report, never re-attached to a later unrelated
// one. Returns e.g. "route 500 upstream_unavailable, status 400 no_flight x2", or null
// when the lookup came back empty with nothing to explain it (a genuinely unknown flight).
export const describeRecentFlightNerveFailures = flight => {
  const tag = flightTagOf(flight)
  const cutoff = Date.now() - FAILURE_RELEVANCE_MS
  const relevant = []
  const otherFlights = []
  recentFailures.forEach(failure => {
    if (failure.at < cutoff) return
    // Untagged entries belong to whoever drains first, exactly as before — so a caller
    // that cannot name its flight still behaves the way it always did. A TAGGED entry is
    // only ever taken by its own flight, and anything else is put back for the lookup it
    // actually describes.
    if (!tag || !failure.flight || failure.flight === tag)
      relevant.push(failure)
    else otherFlights.push(failure)
  })
  recentFailures = otherFlights
  if (!relevant.length) return null

  const counts = new Map()
  relevant.forEach(({ endpoint, status, code }) => {
    const label = `${endpoint} ${status || '?'} ${code}`
    counts.set(label, (counts.get(label) || 0) + 1)
  })
  return Array.from(counts.entries())
    .map(([label, count]) => (count > 1 ? `${label} x${count}` : label))
    .join(', ')
}

// Non-draining peek for the component's failed-lookup latch: a lookup that died
// on a 5xx / timeout / over-quota blip is retryable in seconds — it says nothing
// about the flight — so the latch holds it briefly instead of the full window.
// 4xx responses and semantic notes (status 200) are deterministic answers about
// the flight itself and keep the full latch. Deliberately does NOT drain the
// buffer: the Slack reporter's describeRecentFlightNerveFailures() drain runs
// after this check in the same failure turn and must still see the entries.
export const hadRecentTransientFlightNerveFailure = flight => {
  const tag = flightTagOf(flight)
  const cutoff = Date.now() - FAILURE_RELEVANCE_MS
  return recentFailures.some(
    failure =>
      failure.at >= cutoff &&
      // Scoped to THIS flight, for the same reason the drain is. Since the drain stopped
      // clearing the whole buffer, another flight's entries now survive it — and an
      // unrelated 5xx reaching this peek would downgrade a DETERMINISTIC failure to the
      // short transient latch, re-firing a lookup that cannot succeed and spending quota.
      // Untagged entries still count for everyone, exactly as before.
      (!tag || !failure.flight || failure.flight === tag) &&
      (failure.status === null ||
        failure.status >= 500 ||
        failure.status === 429)
  )
}

// ── RD-4073: "real flight, wrong airport" ───────────────────────────────────
// When the caller passes the booked airport as a leg hint, the API rejects a real flight
// that does not touch it with 400 `airport_mismatch` and drops the flight — so the screen
// could only say "Flight not verified", never the ticket's "doesn't match your itinerary" /
// "This flight doesn't land at LHR". The flight's real airports are not lost, though: the
// /route call made at the start of the same lookup already returned them. Remember that
// answer per flight so a consumer can turn the 400 back into an airport mismatch.
//
// A route is a fact about the flight number, not about one lookup, so it is kept longer
// than a failure — and the lookup's own failures still have to be recent to count.
const ROUTE_RELEVANCE_MS = 10 * 60 * 1000
const MAX_TRACKED_ROUTES = 20
const recentRoutes = new Map()
// Kept apart from `recentFailures`, which the Slack reporter DRAINS a few seconds after an
// error — and outliving FlightTracking's 30s failed-lookup latch, whose re-runs make no
// request at all. Read from the drained buffer, a second Search click on the same wrong
// airport would flip the field from the mismatch to "Flight not verified".
const AIRPORT_MISMATCH_RELEVANCE_MS = 60 * 1000
const airportMismatchAt = new Map()
function rememberAirportMismatch(flight) {
  const tag = flightTagOf(flight)
  if (!tag) return
  airportMismatchAt.set(tag, Date.now())
  if (airportMismatchAt.size > MAX_TRACKED_ROUTES) {
    airportMismatchAt.delete(airportMismatchAt.keys().next().value)
  }
}

const rememberRoute = (flight, route) => {
  const tag = flightTagOf(flight)
  if (!tag || !route || !route.departure || !route.arrival) return
  recentRoutes.delete(tag)
  recentRoutes.set(tag, { at: Date.now(), route })
  if (recentRoutes.size > MAX_TRACKED_ROUTES) {
    recentRoutes.delete(recentRoutes.keys().next().value)
  }
}

// Returns the flight's route only when THIS flight's lookup was rejected as
// `airport_mismatch` within the last minute; otherwise null.
export const peekAirportMismatchRoute = flight => {
  const tag = flightTagOf(flight)
  if (!tag) return null
  const rejectedAt = airportMismatchAt.get(tag)
  if (!rejectedAt || rejectedAt < Date.now() - AIRPORT_MISMATCH_RELEVANCE_MS) {
    return null
  }
  const entry = recentRoutes.get(tag)
  return entry && entry.at >= Date.now() - ROUTE_RELEVANCE_MS
    ? entry.route
    : null
}

// Wraps GET flightNerve/route (Phase 0) — resolves a flight's canonical
// departure/arrival airport pair in one call, replacing the old airlabs
// resolveAirlabsRoute/resolveDepapPreLookup guessing chain outright.
export const fetchFlightNerveRoute = async ({
  num,
  name,
  signal,
  flightTag,
}) => {
  try {
    const { data } = await api.get(
      `flightNerve/route?num=${encodeURIComponent(
        num
      )}&name=${encodeURIComponent(name)}`,
      true,
      false,
      '',
      undefined,
      signal
    )
    rememberRoute(flightTag || `${name}${num}`, data)
    return data
  } catch (error) {
    console.log('fetchFlightNerveRoute error', error)
    recordFailure('route', error, flightTag || `${name}${num}`)
    return null
  }
}

// Wraps GET flightNerve/schedules — FlightNerve's projected per-date timetable, used to
// correct /airline's placeholder times for flights that do not run to a fixed daily time.
// See flightNerveSchedules.js for why this is needed and when it is applied.
export const fetchFlightNerveSchedules = async ({
  num,
  name,
  date,
  days,
  signal,
  flightTag,
}) => {
  try {
    let url = `flightNerve/schedules?num=${encodeURIComponent(
      num
    )}&name=${encodeURIComponent(name)}`
    if (date) url += `&date=${encodeURIComponent(date)}`
    if (days) url += `&days=${encodeURIComponent(days)}`
    const { data } = await api.get(url, true, false, '', undefined, signal)
    return data
  } catch (error) {
    console.log('fetchFlightNerveSchedules error', error)
    recordFailure('schedules', error, flightTag || `${name}${num}`)
    return null
  }
}

// Wraps GET flightNerve/status (Phase 0) — returns the canonical
// {noData, status, departure, arrival, aircraft, durationMinutes, progressPercentage}
// shape, or null on any request failure.
// expectedArrivalAirport / expectedDepartureAirport are optional IATA hints that
// tell the backend which LEG of a multi-stop flight number we mean (e.g. SQ26 flies
// SIN->FRA->JFK on one day; a VIP arrival at JFK must resolve the FRA->JFK leg).
// They are only used to disambiguate; they never filter a single-leg response.
export const fetchFlightNerveStatus = async ({
  num,
  name,
  date,
  depap,
  expectedArrivalAirport,
  expectedDepartureAirport,
  signal,
  flightTag,
}) => {
  try {
    let url = `flightNerve/status?num=${encodeURIComponent(
      num
    )}&name=${encodeURIComponent(name)}`
    if (date) url += `&date=${encodeURIComponent(date)}`
    if (depap) url += `&depap=${encodeURIComponent(depap)}`
    if (expectedArrivalAirport)
      url += `&expectedArrival=${encodeURIComponent(expectedArrivalAirport)}`
    if (expectedDepartureAirport)
      url += `&expectedDeparture=${encodeURIComponent(
        expectedDepartureAirport
      )}`
    const { data } = await api.get(url, true, false, '', undefined, signal)
    return data
  } catch (error) {
    console.log('fetchFlightNerveStatus error', error)
    recordFailure('status', error, flightTag || `${name}${num}`)
    return null
  }
}
